/**
 * RfSpectrum — Wireless > RF Spectrum (live per-AP radios).
 *
 * Reads /api/v2/rf (same owgw source as Clients) and shows each AP's radios: band,
 * channel, width, TX power, channel utilization, and associated clients. A per-band
 * channel summary sits on top so an operator can spot channel overlap at a glance.
 * Read-only; refreshes on a modest cadence.
 */
import { useRf, useRfRecommendations, useApplyDeviceRadio, type RfRadio, type RfRecommendation } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";

export default function RfSpectrum() {
  const { data, isLoading, error } = useRf();
  const radios = data?.items ?? [];

  // Per-band channel occupancy (how many radios sit on each channel) for the summary strip.
  const byBand = new Map<string, Map<number, number>>();
  radios.forEach((r) => {
    if (r.channel == null) return;
    const band = r.band ?? "—";
    if (!byBand.has(band)) byBand.set(band, new Map());
    const m = byBand.get(band)!;
    m.set(r.channel, (m.get(r.channel) ?? 0) + 1);
  });

  return (
    <div>
      <PageHeader
        title="RF Spectrum"
        subtitle={data ? `${radios.length} radios across ${new Set(radios.map((r) => r.device_serial)).size} APs` : "Live radio state per AP"}
      />

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Reading radios…</div>}

      {data && radios.length === 0 && (
        <EmptyState
          title="No live radio data"
          hint="RF state comes from the APs via owgw. When APs are online and reporting, their radios (channel, width, TX power, utilization) appear here."
        />
      )}

      {byBand.size > 0 && (
        <div className="mb-4 flex flex-wrap gap-3">
          {[...byBand.entries()].map(([band, chans]) => (
            <div key={band} className="md-card p-3">
              <div className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">{band}</div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {[...chans.entries()].sort((a, b) => a[0] - b[0]).map(([ch, count]) => (
                  <span key={ch}
                    className={`rounded px-2 py-0.5 font-mono text-xs ${count > 1 ? "bg-warn-soft text-warn" : "bg-surface-2 text-ink-muted"}`}
                    title={count > 1 ? `${count} radios on channel ${ch} (overlap)` : `channel ${ch}`}>
                    {ch}{count > 1 ? `×${count}` : ""}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <AutoRfPanel />

      {radios.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-ink-subtle">
              <tr className="[&>th]:py-1.5 [&>th]:pr-4 [&>th]:font-medium">
                <th>AP</th><th>Band</th><th>Channel</th><th>Width</th><th>TX Power</th><th>Utilization</th><th>Clients</th>
              </tr>
            </thead>
            <tbody>
              {radios.map((r, i) => <RadioRow key={`${r.device_serial}-${i}`} r={r} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Auto-RF: quietest-channel recommendations from the scanned neighbor environment.
 *  Advisory — leads with the radios where a channel change is worth making. */
function AutoRfPanel() {
  const { data } = useRfRecommendations();
  const recs = data?.items ?? [];
  if (recs.length === 0) return null;

  const moves = recs.filter((r) => r.suggest_change);
  return (
    <section aria-label="Auto-RF channel recommendations" className="mb-5">
      <h2 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
        Auto-RF · channel recommendations
      </h2>
      {moves.length === 0 ? (
        <div className="md-card flex items-center gap-3 p-3 text-sm text-ink-muted">
          <span className="inline-block h-2 w-2 rounded-full bg-healthy" />
          All radios are on the least-congested channel for their neighborhood.
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {moves.map((r, i) => <RecRow key={`${r.serial}-${r.band}-${i}`} r={r} />)}
        </ul>
      )}
    </section>
  );
}

function RecRow({ r }: { r: RfRecommendation }) {
  const apply = useApplyDeviceRadio();
  const done = apply.isSuccess;
  return (
    <li className="md-card flex flex-wrap items-center gap-x-4 gap-y-1 p-3">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-ink">{r.name}</div>
        <div className="text-xs text-ink-subtle">{r.band} · {r.neighbors_considered} neighbors heard</div>
      </div>
      <div className="flex items-center gap-2 font-mono text-sm">
        <span className="rounded bg-surface-2 px-2 py-0.5 text-ink-muted" title="Current channel">ch {r.current ?? "—"}</span>
        <span aria-hidden className="text-ink-subtle">→</span>
        <span className="rounded bg-brand-soft px-2 py-0.5 font-semibold text-brand" title="Recommended channel">ch {r.recommended}</span>
      </div>
      <RoleGate min="site_operator" mode="hide">
        <Button
          variant={done ? "ghost" : "primary"}
          disabled={apply.isPending || done || r.band == null}
          onClick={() => r.band && apply.mutate({ serial: r.serial, band: r.band, channel: r.recommended })}
          title={`Set ${r.name} ${r.band} to channel ${r.recommended}`}
        >
          {apply.isPending ? "Applying…" : done ? "Applied ✓" : `Apply ch ${r.recommended}`}
        </Button>
      </RoleGate>
      {apply.isError && <span className="text-xs text-critical">Could not apply.</span>}
    </li>
  );
}

function RadioRow({ r }: { r: RfRadio }) {
  const util = r.utilization_pct;
  const utilColor = util == null ? "text-ink-subtle" : util >= 70 ? "text-critical" : util >= 40 ? "text-warn" : "text-ink-muted";
  return (
    <tr className="border-t border-line/60 [&>td]:py-2 [&>td]:pr-4">
      <td className="font-medium text-ink">{r.device_name}</td>
      <td className="text-ink-muted">{r.band ?? "—"}</td>
      <td className="font-mono text-ink">{r.channel ?? "—"}</td>
      <td className="font-mono text-ink-muted">{r.channel_width ? `${r.channel_width} MHz` : "—"}</td>
      <td className="font-mono text-ink-muted">{r.tx_power != null ? `${r.tx_power} dBm` : "—"}</td>
      <td className={`font-mono ${utilColor}`}>{util != null ? `${util}%` : "—"}</td>
      <td className="font-mono text-ink-muted">{r.clients}</td>
    </tr>
  );
}
